import { useParams, Link } from "react-router-dom";
import DocumentMeta from "@/components/base/DocumentMeta";
import SchemaOrg from "@/components/base/SchemaOrg";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories, getCategoryBySlug } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";
import { useState } from "react";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = getCategoryBySlug(slug || "");
  const [activeSlug, setActiveSlug] = useState(slug || "");

  const allCategories = categories;
  const articles = getArticlesByCategory(activeSlug);

  const handleCategoryClick = (catSlug: string) => {
    setActiveSlug(catSlug);
  };

  const siteUrl = "https://worldcup-tickets-guide.com";
  const categoryUrl = `${siteUrl}/category/${activeSlug}`;

  return (
    <>
      <DocumentMeta
        lang="zh-CN"
        title={activeCategory ? `${activeCategory.name} - 世界杯门票购买指南` : "分类 - 世界杯票务指南"}
        description={activeCategory?.description || "世界杯门票购买指南分类页面"}
        keywords={activeCategory?.keywords.join(",") || "世界杯门票购买"}
        canonical={categoryUrl}
        ogTitle={activeCategory ? `${activeCategory.name} - 世界杯票务指南` : "分类 - 世界杯票务指南"}
        ogDescription={activeCategory?.description || ""}
        ogType="website"
        ogUrl={categoryUrl}
        ogSiteName="世界杯票务指南"
        ogLocale="zh_CN"
      />

      <SchemaOrg
        type="WebPage"
        data={{
          name: activeCategory ? `${activeCategory.name} - 世界杯票务指南` : "分类页面",
          url: categoryUrl,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
            { "@type": "ListItem", position: 2, name: activeCategory?.name || "分类" },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: activeCategory?.name || "分类" },
              ]}
            />

            <div className="mt-6 mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {activeCategory?.name || "购票指南分类"}
              </h1>
              {activeCategory && (
                <p className="text-foreground-600 text-sm mt-2">
                  {activeCategory.description}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {allCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {articles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          {article.publishDate}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          {article.readTime} 分钟阅读
                        </span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                        {article.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                  <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}